/**
 * 7-day strip (Sunday-start) with ‹ › week paging. Today gets a focus-color
 * ring; the selected day gets a translucent blue fill with
 * interactive.primaryHover ink (NOT onColor — the fill is translucent, see
 * the onColor docs; NOT text.link — it fails 4.5:1 on the tint in light)
 * and hides its dot because its list is already showing below.
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { toDateKey } from '@/utils/residents/todoCalendar';

const WEEKDAY_INITIALS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface WeekStripProps {
  weekDays: Date[];
  rangeLabel: string;
  selectedKey: string;
  todayKey: string;
  countsByKey: Record<string, number>;
  onSelectDay: (d: Date) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

const dayAccessibilityLabel = (d: Date, count: number): string => {
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return `${datePart}, ${count} ${count === 1 ? 'todo' : 'todos'}`;
};

export const WeekStrip: React.FC<WeekStripProps> = React.memo(
  ({
    weekDays,
    rangeLabel,
    selectedKey,
    todayKey,
    countsByKey,
    onSelectDay,
    onPrevWeek,
    onNextWeek,
  }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        container: {
          gap: space.sm,
        },
        headerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        arrow: {
          paddingHorizontal: space.md,
          paddingVertical: space.xs,
        },
        arrowText: {
          ...type.cardTitleLarge,
          color: colors.text.secondary,
        },
        rangeLabel: {
          ...type.small,
          color: colors.text.secondary,
        },
        daysRow: {
          flexDirection: 'row',
          gap: space.xs,
        },
        dayCell: {
          flex: 1,
          alignItems: 'center',
          paddingVertical: space.sm,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: 'transparent',
          gap: space.xs,
        },
        dayCellToday: {
          borderColor: semantic.border.focus,
        },
        dayCellSelected: {
          backgroundColor: colors.accent.blueBgStrong,
          borderColor: 'transparent',
        },
        weekdayLabel: {
          ...type.captionMedium,
          color: semantic.text.secondary,
        },
        weekdayLabelSelected: {
          color: semantic.interactive.primaryHover,
        },
        dayNumber: {
          ...type.cardTitle,
          color: colors.text.secondary,
        },
        dayNumberToday: {
          color: colors.text.primary,
        },
        dayNumberSelected: {
          color: semantic.interactive.primaryHover,
        },
        dot: {
          width: 5,
          height: 5,
          borderRadius: radius.full,
          backgroundColor: semantic.status.info,
        },
        dotSpacer: {
          width: 5,
          height: 5,
        },
      }),
    );

    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.arrow}
            onPress={onPrevWeek}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Previous week"
          >
            <Text style={styles.arrowText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.rangeLabel}>{rangeLabel}</Text>
          <TouchableOpacity
            style={styles.arrow}
            onPress={onNextWeek}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Next week"
          >
            <Text style={styles.arrowText}>›</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.daysRow}>
          {weekDays.map((day, index) => {
            const key = toDateKey(day);
            const isSelected = key === selectedKey;
            const isToday = key === todayKey;
            const count = countsByKey[key] ?? 0;
            const showDot = count > 0 && !isSelected;
            return (
              <TouchableOpacity
                key={key}
                style={[
                  styles.dayCell,
                  isToday && styles.dayCellToday,
                  isSelected && styles.dayCellSelected,
                ]}
                onPress={() => onSelectDay(day)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={dayAccessibilityLabel(day, count)}
              >
                <Text
                  style={[
                    styles.weekdayLabel,
                    isSelected && styles.weekdayLabelSelected,
                  ]}
                >
                  {WEEKDAY_INITIALS[index]}
                </Text>
                <Text
                  style={[
                    styles.dayNumber,
                    isToday && styles.dayNumberToday,
                    isSelected && styles.dayNumberSelected,
                  ]}
                >
                  {String(day.getDate())}
                </Text>
                {showDot ? (
                  <View style={styles.dot} testID={`todo-dot-${key}`} />
                ) : (
                  <View style={styles.dotSpacer} />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  },
);

WeekStrip.displayName = 'WeekStrip';
